import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import {
  IoAdd,
  IoChevronBack,
  IoChevronForward,
  IoClose,
  IoOpenOutline,
  IoRemove,
} from 'react-icons/io5'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PdfLightboxProps {
  isOpen: boolean
  fileUrl: string
  title?: string
  onClose: () => void
}

const MIN_SCALE = 0.5
const MAX_SCALE = 3
const SCALE_STEP = 0.25

const PdfLightbox: React.FC<PdfLightboxProps> = ({ isOpen, fileUrl, title, onClose }) => {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1)
  const [containerWidth, setContainerWidth] = useState<number>(0)

  const viewportRef = useRef<HTMLDivElement>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setPageNumber(1)
      setScale(1)
      setNumPages(0)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const updateWidth = () => {
      if (viewportRef.current) {
        const w = viewportRef.current.clientWidth
        setContainerWidth(Math.max(0, w - 16))
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [isOpen])

  const onDocumentLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n)
  }, [])

  const goPrev = useCallback(() => setPageNumber((p) => Math.max(1, p - 1)), [])
  const goNext = useCallback(() => setPageNumber((p) => Math.min(numPages, p + 1)), [numPages])
  const zoomOut = useCallback(() => setScale((s) => Math.max(MIN_SCALE, +(s - SCALE_STEP).toFixed(2))), [])
  const zoomIn = useCallback(() => setScale((s) => Math.min(MAX_SCALE, +(s + SCALE_STEP).toFixed(2))), [])
  const resetZoom = useCallback(() => setScale(1), [])

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current || scale !== 1) {
      touchStartRef.current = null
      return
    }
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y
    touchStartRef.current = null

    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNext()
      else goPrev()
    }
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.85)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'relative',
          width: 'min(1100px, 96vw)',
          height: '92vh',
          backgroundColor: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid #eee',
            backgroundColor: '#fafafa',
            gap: '10px',
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: '14px',
              color: '#333',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title || 'Visualização do documento'}
          </span>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              title="Abrir em nova aba"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#5673b5',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '34px',
                height: '34px',
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              <IoOpenOutline size={18} />
            </a>
            <button
              type="button"
              title="Fechar"
              style={{
                background: '#FA5A84',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '34px',
                height: '34px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={onClose}
            >
              <IoClose size={20} />
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderBottom: '1px solid #eee',
            backgroundColor: '#fff',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={goPrev}
            disabled={pageNumber <= 1}
            title="Página anterior"
            style={pdfToolbarButtonStyle(pageNumber <= 1)}
          >
            <IoChevronBack size={16} />
          </button>
          <span style={{ fontSize: '13px', color: '#555', minWidth: '90px', textAlign: 'center' }}>
            Página {pageNumber} / {numPages || '—'}
          </span>
          <button
            type="button"
            onClick={goNext}
            disabled={!numPages || pageNumber >= numPages}
            title="Próxima página"
            style={pdfToolbarButtonStyle(!numPages || pageNumber >= numPages)}
          >
            <IoChevronForward size={16} />
          </button>

          <div style={{ width: 1, height: 22, background: '#ddd', margin: '0 6px' }} />

          <button
            type="button"
            onClick={zoomOut}
            disabled={scale <= MIN_SCALE}
            title="Diminuir zoom"
            style={pdfToolbarButtonStyle(scale <= MIN_SCALE)}
          >
            <IoRemove size={16} />
          </button>
          <button
            type="button"
            onClick={resetZoom}
            title="Restaurar zoom"
            style={{
              ...pdfToolbarButtonStyle(false),
              width: 'auto',
              padding: '0 8px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            type="button"
            onClick={zoomIn}
            disabled={scale >= MAX_SCALE}
            title="Aumentar zoom"
            style={pdfToolbarButtonStyle(scale >= MAX_SCALE)}
          >
            <IoAdd size={16} />
          </button>
        </div>

        <div
          ref={viewportRef}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#525659',
            display: 'flex',
            justifyContent: 'center',
            padding: '8px',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<div style={{ color: '#fff', padding: 20 }}>Carregando PDF...</div>}
            error={<div style={{ color: '#fff', padding: 20 }}>Erro ao carregar PDF.</div>}
          >
            {containerWidth > 0 && (
              <Page
                pageNumber={pageNumber}
                width={containerWidth * scale}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            )}
          </Document>
        </div>
      </div>
    </div>
  )
}

const pdfToolbarButtonStyle = (disabled: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '30px',
  height: '30px',
  borderRadius: '6px',
  border: '1px solid #ddd',
  background: disabled ? '#f4f4f4' : '#fff',
  color: disabled ? '#bbb' : '#333',
  cursor: disabled ? 'not-allowed' : 'pointer',
})

export default PdfLightbox
